import React, { useMemo, useState } from 'react'
import { Box, Button, ListItemIcon, ListItemText, Menu, MenuItem, Typography } from '@mui/material'
import { useTranslation } from '../../shared/hooks/useIntractionTranslate'
import { INTERAX_COLORS } from '../../constants/colors'
import { ChevronDownIcon, CheckIcon } from '@heroicons/react/24/outline'

export interface LanguageSwitcherProps {
  className?: string
}

const LANGUAGES = [
  { code: 'en', labelKey: 'languages.english', flag: '🇺🇸' },
  { code: 'sv', labelKey: 'languages.svenska', flag: '🇸🇪' }
] as const

export const LanguageSwitcher: React.FC<LanguageSwitcherProps> = ({ className = '' }) => {
  const { i18n, t } = useTranslation()
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const open = Boolean(anchorEl)

  const languages = useMemo(
    () =>
      LANGUAGES.map((lang) => ({
        ...lang,
        label: t(lang.labelKey)
      })),
    [t]
  )

  const current = languages.find((lang) => lang.code === i18n.language) ?? languages[0]

  const handleOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => setAnchorEl(null)

  const handleSelect = (code: string) => {
    if (i18n.language !== code) {
      void i18n.changeLanguage(code)
    }
    handleClose()
  }

  return (
    <>
      <Button
        className={className}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        aria-controls={open ? 'language-switcher-menu' : undefined}
        onClick={handleOpen}
        sx={{
          textTransform: 'none',
          display: 'inline-flex',
          alignItems: 'center',
          gap: 1,
          px: 1.5,
          py: 1.0,
          borderRadius: '12px',
          border: `1px solid ${INTERAX_COLORS.thunderGrey}`,
          backgroundColor: 'transparent',
          color: INTERAX_COLORS.white,
          '&:hover': {
            backgroundColor: `${INTERAX_COLORS.white10}`
          }
        }}
        startIcon={<Typography component="span">{current.flag}</Typography>}
        endIcon={
          <Box
            component={ChevronDownIcon}
            sx={{
              width: 16,
              height: 16,
              transition: 'transform 150ms',
              transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
              color: INTERAX_COLORS.thunderGrey
            }}
          />
        }
      >
        <Typography variant="body2" sx={{ fontWeight: 500, color: INTERAX_COLORS.white }}>
          {current.label}
        </Typography>
      </Button>

      <Menu
        id="language-switcher-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        PaperProps={{
          sx: {
            minWidth: 200,
            mt: 1,
            borderRadius: '12px',
            backgroundColor: INTERAX_COLORS.offBlack,
            color: INTERAX_COLORS.white
          }
        }}
      >
        {languages.map((language) => {
          const isSelected = language.code === current.code
          return (
            <MenuItem
              key={language.code}
              onClick={() => handleSelect(language.code)}
              selected={isSelected}
              sx={{
                display: 'flex',
                gap: 1.5,
                '&.Mui-selected': {
                  backgroundColor: INTERAX_COLORS.sun20,
                  color: INTERAX_COLORS.sun
                },
                '&:hover': {
                  backgroundColor: INTERAX_COLORS.white10
                }
              }}
            >
              <ListItemIcon sx={{ minWidth: 28, color: 'inherit' }}>
                <Typography component="span" sx={{ fontSize: '1.25rem' }}>
                  {language.flag}
                </Typography>
              </ListItemIcon>
              <ListItemText
                primary={language.label}
                primaryTypographyProps={{ variant: 'body2', fontWeight: isSelected ? 600 : 400 }}
              />
              {isSelected && (
                <ListItemIcon sx={{ minWidth: 24, justifyContent: 'flex-end', color: INTERAX_COLORS.sun }}>
                  <CheckIcon style={{ width: 16, height: 16 }} />
                </ListItemIcon>
              )}
            </MenuItem>
          )
        })}
      </Menu>
    </>
  )
}

export default LanguageSwitcher
